import { useEffect } from 'react';
import { Platform } from 'react-native';
import { AlertButton, useWebAlert } from './WebAlert';
import { SILENT_PDF_DOWNLOAD_EVENT } from '@/utils/pdfDownloadEvents';
import { showToast } from '@/utils/toast';
import { PDFServiceWebLogger } from '@/utils/logger';

const PdfDownloadPromptListener = () => {
  const { showAlert } = useWebAlert();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return undefined;
    }

    const handler = (event: WindowEventMap[typeof SILENT_PDF_DOWNLOAD_EVENT]) => {
      const detail = event.detail;
      if (!detail) return;

      const { blob, blobUrl, fileName, messageHint } = detail;
      let released = false;
      const releaseUrl = () => {
        if (released) return;
        released = true;
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (err) {
          PDFServiceWebLogger.Main.warn('[PdfDownloadPrompt] Falha ao liberar blobUrl', { err });
        }
      };

      const canShare =
        typeof navigator !== 'undefined' &&
        typeof (navigator as any).share === 'function' &&
        typeof File !== 'undefined';

      const openPdf = () => {
        PDFServiceWebLogger.Main.info('[PdfDownloadPrompt] Usuário optou por abrir o PDF', { fileName });
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        releaseUrl();
      };

      const sharePdf = async () => {
        try {
          const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
          const nav: any = navigator;
          if (typeof nav.canShare === 'function' && !nav.canShare({ files: [file] })) {
            throw new Error('Compartilhamento indisponível');
          }
          await nav.share({
            files: [file],
            title: 'Checklist em PDF',
            text: 'PDF gerado pelo App Guincho',
          });
          showToast('PDF compartilhado com sucesso', { type: 'success', duration: 1600 });
        } catch (err: any) {
          if (err?.name === 'AbortError') {
            showToast('Compartilhamento cancelado', { type: 'info', duration: 1400 });
          } else {
            showToast('Não foi possível compartilhar agora', { type: 'error', duration: 2000 });
          }
        } finally {
          releaseUrl();
        }
      };

      const buttons: AlertButton[] = [
        {
          text: 'Abrir PDF',
          onPress: openPdf,
        },
      ];

      if (canShare) {
        buttons.push({
          text: 'Compartilhar',
          onPress: () => {
            void sharePdf();
          },
        });
      }

      buttons.push({
        text: 'Fechar',
        style: 'cancel',
        onPress: releaseUrl,
      });

      const message = `${messageHint}\n\nDeseja abrir ou compartilhar agora?`;
      PDFServiceWebLogger.Main.info('[PdfDownloadPrompt] Exibindo alerta pós-download', {
        fileName,
        canShare,
      });
      showAlert('PDF salvo em Downloads', message, buttons);
    };

    window.addEventListener(SILENT_PDF_DOWNLOAD_EVENT, handler);
    return () => {
      window.removeEventListener(SILENT_PDF_DOWNLOAD_EVENT, handler);
    };
  }, [showAlert]);

  return null;
};

export default PdfDownloadPromptListener;
